import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CompanyApiService, HTTP_AP_CreateCompany_Request } from '@api-new/companyservice';
import { HTTP_X_GenerateUploadUrl_Response } from '@api-new/documentservice';
import { RoutePathsEnum } from '@app/app.utils';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CreateCompanyFormValues } from '@platform/company/pages/company-create/forms/create-company.form';
import { ErrorModel } from '@shared-lib/models/error.model';
import { SharedFileModel } from '@shared-lib/models/shared-file.model';
import { FileUploadService } from '@shared-lib/services/file-upload.service';
import { ToastActionEnum, ToastService, ToastSeverityEnum } from '@shared-lib/services/toast.service';
import { TOAST_MESSAGE } from '@shared/constants/toast-messages';
import { setPlatformType } from '@shared/store/platform/platform.actions';
import { companyTypeToPlatformType } from '@shared/utils/company-type-to-platform-type';
import { forkJoin, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { createCompanyFormToHttpRequestAdapter } from '../adapters/create-company-form-to-http-request.adapter';
import * as CompanyActions from './company.actions';

@Injectable()
export class CompanyEffects {
  private readonly actions$ = inject(Actions);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly fileUploadService = inject(FileUploadService);
  private readonly companyService = inject(CompanyApiService);

  getCompanyById = createEffect(() => {
    return this.actions$.pipe(
      ofType(CompanyActions.getCompanyById),
      filter(({ id }) => id != null),
      switchMap(({ id }) =>
        this.companyService.HTTP_AP_GetCompany_ForDetails({ companyId: id }).pipe(
          map((company) => CompanyActions.getCompanyByIdSuccess({ company })),
          catchError((error: ErrorModel) => {
            this.toastService.showToast(ToastSeverityEnum.Error, TOAST_MESSAGE.ERROR.getting_data);
            return of(CompanyActions.getCompanyByIdFailure({ error }));
          }),
        ),
      ),
    );
  });

  handleGetCompanyByIdSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyActions.getCompanyByIdSuccess),
      map(({ company }) => setPlatformType({ platformType: companyTypeToPlatformType(company.companyType) })),
    ),
  );

  createCompany = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyActions.createCompany),
      mergeMap(({ company }) => forkJoin([this.fileUploadService.requestFileUpload(company.newLogo), of(company)])), // upload logo image
      map(([uploadResponse, companyWithoutLogo]: [HTTP_X_GenerateUploadUrl_Response, CreateCompanyFormValues]) =>
        createCompanyFormToHttpRequestAdapter(companyWithoutLogo, uploadResponse.uploadUrl),
      ),
      switchMap((dataToUpdate: HTTP_AP_CreateCompany_Request) =>
        this.companyService.HTTP_AP_CreateCompany(dataToUpdate).pipe(
          map((createdCompany) => {
            this.toastService.showToast(ToastSeverityEnum.Success, TOAST_MESSAGE.SUCCESS[ToastActionEnum.ActionCreate]('Company'));
            void this.router.navigate([RoutePathsEnum.Platform, RoutePathsEnum.Companies, createdCompany.id]);
            // Loading all company list one more time after successful upload
            return CompanyActions.createCompanySuccess({ company: createdCompany });
          }),
          catchError((error: ErrorModel) => {
            this.toastService.showToast(ToastSeverityEnum.Error, TOAST_MESSAGE.ERROR[ToastActionEnum.ActionCreate]('Company'));
            return of(CompanyActions.createCompanyFailure({ error }));
          }),
        ),
      ),
    ),
  );

  updateCompanyLogo = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyActions.updateCompanyLogo),
      switchMap(({ companyId, file }) =>
        this.fileUploadService.requestFileUpload(file).pipe(
          switchMap(({ uploadUrl }: HTTP_X_GenerateUploadUrl_Response) =>
            this.companyService.HTTP_AP_UpdateCompanyLogo({ companyId, logoUploadUrl: uploadUrl }),
          ),
          map((company) => {
            this.toastService.showToast(ToastSeverityEnum.Success, TOAST_MESSAGE.SUCCESS[ToastActionEnum.ActionUpdate]('Company logo'));
            return CompanyActions.updateCompanyLogoSuccess({ company });
          }),
          catchError((error: ErrorModel) => {
            this.toastService.showToast(ToastSeverityEnum.Error, TOAST_MESSAGE.ERROR[ToastActionEnum.ActionUpdate]('Company logo'));
            return of(CompanyActions.updateCompanyLogoFailure({ error }));
          }),
        ),
      ),
    ),
  );
}
